import Select from "@/v2-ui/select"
import Tooltip from "@/v2-ui/tooltip"
import { useCustomerUserQuery } from "@/v2-backoffice-client/customer/user/customer.user.query"

/** The picked person, as the rows will name them. */
export type CustomerFeatureAccessPickedUser = {
  id: number
  firstName: string
}

export type CustomerFeatureAccessUserPickerProps = {
  customerId: number
  customerName: string
  picked: CustomerFeatureAccessPickedUser | null
  onPick(user: CustomerFeatureAccessPickedUser | null): void
  /** The seats each person holds, by product name; marks them in the list. */
  seatProductsByUserId: ReadonlyMap<number, string[]>
  /** The seated products the customer holds a live license of, for the "no seat" mark. */
  seatedProductNames: readonly string[]
}

const EVERYONE = "everyone"

function joinNames(names: readonly string[], conjunction: string) {
  if (names.length <= 1) return names.join("")
  return `${names.slice(0, -1).join(", ")} ${conjunction} ${names[names.length - 1]}`
}

// "Show access for": everyone at the customer (the default, and what the
// lists show), or one person. Shown only when two users of the customer
// can differ at all — a seated license, a team on the older per-team
// mechanism — because everything on this tab is decided per customer, and
// says so on hover. Each person carries, under their name, why their
// answer can differ: the seat they hold, or that they hold none; without
// that line the picker and the "seat holders only" carrier above looked
// unrelated. Owns the users read, so it runs only while this control is on
// screen, and hands the caller the picked person with the name the rows
// will say; the caller runs the rule for them and the rows add a "✓ Lina"
// / "✗ Lina" mark beside the customer's answer.
function CustomerFeatureAccessUserPicker(
  props: CustomerFeatureAccessUserPickerProps,
) {
  const {
    customerId,
    customerName,
    picked,
    onPick,
    seatProductsByUserId,
    seatedProductNames,
  } = props
  const { data } = useCustomerUserQuery({ variables: { customerId } })
  const people = (data?.getCustomerUsers ?? []).filter(
    (user) => !user.IsDeleted && !user.IsBlocked,
  )

  function seatMarkOf(userId: number): string | undefined {
    const held = seatProductsByUserId.get(userId)
    if (held && held.length > 0) return `Seat on ${joinNames(held, "and")}`
    if (seatedProductNames.length > 0) {
      return `No seat on ${joinNames(seatedProductNames, "or")}`
    }
    return undefined
  }

  return (
    <label className="flex items-center gap-2 text-xs text-muted-foreground">
      {/* The explanation hangs on the words, not on the select: a tooltip
          over the trigger opened on top of the open list. */}
      <Tooltip>
        <Tooltip.Trigger asChild>
          <span className="shrink-0 cursor-help underline decoration-dotted underline-offset-2">
            Show access for
          </span>
        </Tooltip.Trigger>
        <Tooltip.Content side="top" className="max-w-[340px]">
          Everyone at {customerName} gets the same, except through MCP seats
          (named people on an MCP license). Pick a person to see the rule&apos;s
          exact answer for them beside each row; who holds a seat is marked in
          the list.
        </Tooltip.Content>
      </Tooltip>
      <div className="w-56 shrink-0">
        <Select
          value={picked ? String(picked.id) : EVERYONE}
          onValueChange={(next) => {
            const user = people.find((person) => String(person.Id) === next)
            onPick(
              user
                ? {
                    id: user.Id,
                    firstName: user.FirstName ?? user.Email ?? "",
                  }
                : null,
            )
          }}
        >
          <Select.Trigger
            size="xs"
            className="w-full"
            aria-label="Show feature access for everyone at the customer, or for one person"
            data-feature-code="UI_BackofficeCustomer_FeatureAccess_PreviewAs"
          >
            <Select.Value />
          </Select.Trigger>
          <Select.Content className="max-w-[360px]">
            <Select.Item value={EVERYONE}>
              Everyone at {customerName}
            </Select.Item>
            {people.map((user) => (
              <Select.Item
                key={user.Id}
                value={String(user.Id)}
                description={seatMarkOf(user.Id)}
              >
                {`${user.FirstName ?? ""} ${user.LastName ?? ""}`.trim() ||
                  user.Email}
              </Select.Item>
            ))}
          </Select.Content>
        </Select>
      </div>
    </label>
  )
}

export default CustomerFeatureAccessUserPicker
